/**
 * Config tab (Phase 5 Action 49).
 *
 * Rolls up all the "how this tenant is wired" sections that used to live
 * inline on TenantDetail: plan assignment, Slack connection, Teams
 * placeholder, baseline delivery/routing config. Edit affordances still
 * link out to the dedicated pages (/fleet/:id/plan, /fleet/:id/slack,
 * /fleet/:id/baseline) so this tab stays read-only and skimmable.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Link, useOutletContext } from 'react-router-dom';
import { apiPath } from '../../api/baseUrl';
import { getStoredToken } from '../../auth/AuthContext';
import { fetchJson, formatDate, type TenantContext } from './shared';

interface PlanAssignment {
  id: string;
  plan: { id: string; slug: string; name: string; description: string | null };
  assignedAt: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  dealMetadata: {
    contractUrl?: string;
    mrrUsd?: number;
    billingCycle?: 'monthly' | 'annual' | 'other';
    notes?: string;
  };
}

interface SlackConnection {
  workspaceId: string;
  workspaceName: string;
  botUserId: string;
  connectedAt: string;
  connectedByOperatorId: string | null;
}

interface BaselineResp {
  delivery: { severity_taxonomy?: string[]; default_channels?: string[]; default_schedule?: { cron: string; timezone: string } } | null;
  routing: { default_owner_user_id?: string | null } | null;
}

export default function Config() {
  const { tenant } = useOutletContext<TenantContext>();

  const planQ = useQuery({
    queryKey: ['tenant-plan', tenant.id],
    queryFn: () => fetchJson<{ data: { assignment: PlanAssignment | null } }>(`/api/provisioning/tenants/${tenant.id}/plan`),
  });

  const slackQ = useQuery({
    queryKey: ['tenant-slack', tenant.id],
    queryFn: () => fetchJson<{ data: { connection: SlackConnection | null } }>(`/api/provisioning/tenants/${tenant.id}/slack/connection`),
  });

  const flagsQ = useQuery({
    queryKey: ['tenant-flags', tenant.id],
    queryFn: () => fetchJson<{ data: { flags: Record<string, boolean | string | number | null> } }>(`/api/provisioning/tenants/${tenant.id}/flags`),
  });
  const teamsEnabled =
    flagsQ.data?.data.flags['platforms.teams.connection_enabled_in_wizard'] === true;

  const baselineQ = useQuery({
    queryKey: ['tenant-baseline', tenant.id],
    queryFn: () => fetchJson<{ data: BaselineResp }>(`/api/provisioning/tenants/${tenant.id}/baseline-config`),
  });

  const assignment = planQ.data?.data.assignment ?? null;
  const slackConnection = slackQ.data?.data.connection ?? null;

  return (
    <>
      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Plan</h2>
          <Link
            to={`/fleet/${tenant.id}/plan`}
            className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            {assignment ? 'Change plan' : 'Assign plan'}
          </Link>
        </header>
        <div className="px-6 py-4 text-sm">
          {planQ.isLoading && <div className="text-slate-500">Loading plan…</div>}
          {planQ.isError && (
            <div className="text-red-700">Could not load plan: {planQ.error instanceof Error ? planQ.error.message : 'unknown error'}</div>
          )}
          {planQ.data && !assignment && (
            <div className="text-slate-500">No plan assigned yet. Click "Assign plan" to pick one.</div>
          )}
          {assignment && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-slate-500">Plan</dt>
                <dd className="mt-0.5 text-slate-900">{assignment.plan.name}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Assigned</dt>
                <dd className="mt-0.5 text-slate-900">{formatDate(assignment.assignedAt)}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Contract URL</dt>
                <dd className="mt-0.5 text-slate-900 break-all">
                  {assignment.dealMetadata.contractUrl ? (
                    <a className="text-primary-700 underline" href={assignment.dealMetadata.contractUrl} target="_blank" rel="noreferrer">
                      {assignment.dealMetadata.contractUrl}
                    </a>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">MRR (USD)</dt>
                <dd className="mt-0.5 text-slate-900">
                  {assignment.dealMetadata.mrrUsd !== undefined ? `$${assignment.dealMetadata.mrrUsd.toLocaleString()}` : <span className="text-slate-400">—</span>}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Billing cycle</dt>
                <dd className="mt-0.5 text-slate-900">{assignment.dealMetadata.billingCycle ?? <span className="text-slate-400">—</span>}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-500">Notes</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-slate-900">
                  {assignment.dealMetadata.notes ?? <span className="text-slate-400">—</span>}
                </dd>
              </div>
            </dl>
          )}
        </div>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Slack</h2>
          {!slackQ.isLoading && (
            slackConnection ? (
              <RotateSlackCredentialsButton tenantId={tenant.id} workspaceName={slackConnection.workspaceName} />
            ) : (
              <Link
                to={`/fleet/${tenant.id}/slack`}
                className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                Connect Slack
              </Link>
            )
          )}
        </header>
        <div className="px-6 py-4 text-sm">
          {slackQ.isLoading && <div className="text-slate-500">Checking Slack connection…</div>}
          {slackQ.isError && (
            <div className="text-red-700">
              Could not load Slack state: {slackQ.error instanceof Error ? slackQ.error.message : 'unknown error'}
            </div>
          )}
          {slackQ.data && !slackConnection && (
            <div className="text-slate-500">
              Not connected. Click "Connect Slack" to install the PowerMove app in this tenant's workspace.
            </div>
          )}
          {slackConnection && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <dt className="text-slate-500">Workspace</dt>
                <dd className="mt-0.5 text-slate-900">{slackConnection.workspaceName}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Workspace ID</dt>
                <dd className="mt-0.5 text-slate-900 font-mono">{slackConnection.workspaceId}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Bot user ID</dt>
                <dd className="mt-0.5 text-slate-900 font-mono">{slackConnection.botUserId}</dd>
              </div>
              <div>
                <dt className="text-slate-500">Connected</dt>
                <dd className="mt-0.5 text-slate-900">{formatDate(slackConnection.connectedAt)}</dd>
              </div>
            </dl>
          )}
        </div>
      </section>

      {/* Teams placeholder. Card stays visible so the roadmap is discoverable;
          the connect button flips clickable when the wizard flag is on. */}
      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-slate-900">Teams</h2>
            {!teamsEnabled && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600">
                Coming soon
              </span>
            )}
          </div>
          <button
            type="button"
            disabled={!teamsEnabled}
            onClick={() => {
              if (teamsEnabled) {
                alert('Teams connection coming soon. Adapter ships in a later wave.');
              }
            }}
            className={clsx(
              'rounded-md border px-3 py-1 text-xs',
              teamsEnabled
                ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400',
            )}
          >
            Connect Teams
          </button>
        </header>
        <div className="px-6 py-4 text-sm text-slate-500">
          {teamsEnabled
            ? 'Connect this tenant to a Microsoft Teams workspace. (Adapter not yet implemented — clicking the button shows a placeholder.)'
            : 'Microsoft Teams integration is not yet available. This card stays visible to signal it\'s on the roadmap. When the adapter ships, flip the platforms.teams.connection_enabled_in_wizard flag and the button becomes clickable.'}
        </div>
      </section>

      <section className="mb-6 rounded-md border border-slate-200 bg-white">
        <header className="flex items-center justify-between border-b border-slate-200 px-6 py-3">
          <h2 className="text-sm font-semibold text-slate-900">Baseline config</h2>
          <Link
            to={`/fleet/${tenant.id}/baseline`}
            className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Edit
          </Link>
        </header>
        <div className="px-6 py-4 text-sm">
          {baselineQ.isLoading && <div className="text-slate-500">Loading…</div>}
          {baselineQ.data && (() => {
            const d = baselineQ.data.data.delivery;
            const r = baselineQ.data.data.routing;
            return (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <dt className="text-slate-500">Severity</dt>
                  <dd className="mt-0.5 text-slate-900 font-mono">{d?.severity_taxonomy?.length ? `[${d.severity_taxonomy.join(', ')}]` : <span className="text-slate-400">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Default channels</dt>
                  <dd className="mt-0.5 text-slate-900 font-mono">{d?.default_channels?.length ? d.default_channels.join(', ') : <span className="text-slate-400">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Schedule</dt>
                  <dd className="mt-0.5 text-slate-900 font-mono">{d?.default_schedule?.cron ?? <span className="text-slate-400">—</span>}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Schedule timezone</dt>
                  <dd className="mt-0.5 text-slate-900 font-mono">{d?.default_schedule?.timezone ?? <span className="text-slate-400">—</span>}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-slate-500">Default owner</dt>
                  <dd className="mt-0.5 text-slate-900 font-mono">{r?.default_owner_user_id ?? <span className="text-slate-400">—</span>}</dd>
                </div>
              </dl>
            );
          })()}
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Phase 8 Action 53: Rotate Slack credentials
// ---------------------------------------------------------------------------

interface InstallUrlResp {
  data: { installUrl: string; expiresAt: string };
}

function RotateSlackCredentialsButton({ tenantId, workspaceName }: { tenantId: string; workspaceName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
      >
        Rotate credentials
      </button>
      {open && (
        <RotateSlackModal
          tenantId={tenantId}
          workspaceName={workspaceName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RotateSlackModal({
  tenantId,
  workspaceName,
  onClose,
}: {
  tenantId: string;
  workspaceName: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [copied, setCopied] = useState(false);

  const mutation = useMutation<InstallUrlResp, Error, string>({
    mutationFn: async (r) => {
      const token = getStoredToken();
      const res = await fetch(apiPath(`/api/provisioning/tenants/${tenantId}/slack/install-url`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ reason: r }),
      });
      const json = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) throw new Error(json.message ?? `install-url failed: ${res.status}`);
      return json as InstallUrlResp;
    },
    onSuccess: () => {
      // Rotation isn't complete until the customer OAuths in their
      // workspace and hits the callback — but the audit init row is
      // written now. Invalidate slack connection view so if the operator
      // reloads before the callback lands, they see the current state.
      queryClient.invalidateQueries({ queryKey: ['tenant-slack', tenantId] });
    },
  });

  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= 10 && !mutation.isPending;

  function submit() {
    if (!canSubmit) return;
    mutation.mutate(reason.trim());
  }

  function copyUrl() {
    if (!mutation.data) return;
    navigator.clipboard.writeText(mutation.data.data.installUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      // Best-effort — some sandboxes block clipboard writes; operator
      // can still select-and-copy the truncated preview manually.
      () => setCopied(false),
    );
  }

  function closeIfIdle() {
    if (mutation.isPending) return;
    onClose();
  }

  const expiryClock = mutation.data
    ? new Date(mutation.data.data.expiresAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;

  // Truncated URL preview: install URLs are 200+ chars; hide the middle
  // so the modal doesn't run wide. Full URL still copies via clipboard.
  const urlPreview = mutation.data
    ? (() => {
        const u = mutation.data.data.installUrl;
        return u.length > 60 ? `${u.slice(0, 40)}…${u.slice(-16)}` : u;
      })()
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-md bg-white shadow-lg">
        <header className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-base font-semibold text-slate-900">Rotate Slack credentials</h2>
          <p className="mt-1 text-xs text-slate-500">
            Workspace: <strong>{workspaceName}</strong>
          </p>
        </header>

        {/* Two states: pre-submit (reason input) and post-submit (URL to share). */}
        {!mutation.data ? (
          <div className="px-6 py-4">
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>Bot stays connected on the current token</strong> until your workspace admin completes re-authorization. When they do, the old token is revoked immediately.
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
              Reason (audit trail)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g., Quarterly rotation per security policy; previous token 90 days old."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              disabled={mutation.isPending}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={clsx(trimmedLen < 10 ? 'text-slate-400' : 'text-slate-500')}>
                {trimmedLen < 10 ? `${10 - trimmedLen} more character${10 - trimmedLen === 1 ? '' : 's'} required` : 'Ready to generate'}
              </span>
              <span className={clsx('text-slate-400 tabular-nums', trimmedLen > 500 && 'text-red-600')}>
                {trimmedLen} / 500
              </span>
            </div>
            {mutation.isError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {mutation.error.message}
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 py-4">
            <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
              Rotation link generated. <strong>Send this URL to a workspace admin in {workspaceName}.</strong> Link expires at {expiryClock}.
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
              Install URL
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700 font-mono">
                {urlPreview}
              </code>
              <button
                type="button"
                onClick={copyUrl}
                className={clsx(
                  'rounded-md border px-3 py-2 text-xs transition',
                  copied
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50',
                )}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Once the customer completes OAuth, the new token is written and the old token is revoked automatically. The Slack section on this page will refresh.
            </p>
          </div>
        )}

        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-3">
          <button
            type="button"
            onClick={closeIfIdle}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.data ? 'Close' : 'Cancel'}
          </button>
          {!mutation.data && (
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {mutation.isPending ? 'Generating…' : 'Generate install URL'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
